class CompleteWithSectionHandler extends HTMLElement {
  constructor() {
    super();
    this.viewModel = null;
  }

  connectedCallback() {
    this.viewModel = this.getViewModel();
    this.initialize();
  }

  getViewModel() {
    return {
      $completeWithSubmit: document.querySelector('[data-replaceable-complete-with-submit]'),
      $modal: document.getElementById('complete-with-modal-color-product'),
      $card: this.querySelector('[data-replaceable-complete-with-card]')
    };
  }

  initialize() {
    const { $completeWithSubmit, $modal, $card } = this.viewModel;

    $card.addEventListener('click', (event) => {
      if (this.cardImage?.contains(event.target)) {
        return;
      }

      if (this.sizeTrigger?.contains(event.target)) {
        this.handleSizeTrigger();
        return;
      }

      if (this.colorTrigger?.contains(event.target)) {
        this.handleColorTrigger();
        return;
      }

      if (!this.isSizeSelected && this.sizeTrigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // When there is a size selection required open size modal view
        this.handleSizeTrigger();
        $modal?.open();
      } else {
        this.handleCheckboxChange(this.completeWithCheckbox.checked);
      }
    });

    $completeWithSubmit?.addEventListener('click', () => {
      if (!this.isSizeSelected && this.sizeTrigger) {
        this.sizeVariants.classList.add('is-error');
        this.setButtonText(window.variantStrings.select_size);
        return;
      }

      const checkbox = this.completeWithCheckbox;
      checkbox.checked = true;
      this.handleCheckboxChange(true);
      $modal?.close();
    });

    this.addEventListener('variant:updated', (e) => this.onVariantUpdated(e.detail));
  }

  handleSizeTrigger() {
    const { $modal } = this.viewModel;
    $modal?.classList.add('size-modal');
    $modal?.classList.remove('color-modal');
  }

  handleColorTrigger() {
    const { $modal } = this.viewModel;
    $modal?.classList.add('color-modal');
    $modal?.classList.remove('size-modal');
  }

  onVariantUpdated({ variant, size}) {
    if (this.isSizeSelected) {
      this.setButtonAvailable();
      this.sizeVariants.classList.remove('is-error');
    }

    if (variant && (!variant.available || variant.inventory_quantity === 0)) {
      this.setButtonOutOfStock();
      return;
    }

    this.setSelectedVariant(variant)
    this.setSizeLabel(size);
  }

  setSelectedVariant(variant) {
    if (variant?.id) {
      this.completeWithCheckbox.value = variant.id;
    }
  }

  setSizeLabel(size) {
    this.sizeTrigger.classList.add('selected');
    this.sizeTrigger.querySelector('.input-chip__leading-label').textContent = `${window.product.size_label} ${size.reverse().join(', ')}`;
  }

  handleCheckboxChange(isChecked) {
    if (isChecked) {
      this.submitButtonComponent.setDefaultTextValue();
    } else {
      this.submitButtonComponent.setDefaultTextValue();
    }
  }

  setButtonText(text) {
    this.completeWithSubmitLabel.textContent = text;
  }

  setButtonOutOfStock() {
    this.setButtonText(window.variantStrings.outOfStock || 'Out of Stock');
    this.completeWithSubmit.setAttribute('disabled', 'true');
  }

  setButtonAvailable() {
    this.setButtonText(window.variantStrings.done || 'Done');
    this.completeWithSubmit.removeAttribute('disabled');
  }

  get completeWithSubmit() {
    return document.querySelector('[data-replaceable-complete-with-submit] [data-complete-with-done]');
  }

  get completeWithSubmitLabel() {
    return this.completeWithSubmit.querySelector('.shared-button__leading-label');
  }

  get submitButtonComponent() {
    return document.querySelector('add-to-cart-button');
  }

  get isSizeSelected() {
    return this.sizeVariants?.getAttribute('data-size-selected') === 'true';
  }

  get sizeVariants() {
    return document.querySelector('[data-replaceable-complete-with-variants] .variant-selects');
  }

  get completeWithCheckbox() {
    return this.querySelector('input[type="checkbox"]');
  }

  get sizeTrigger() {
    return this.querySelector('.complete-with__size-trigger');
  }

  get cardImage() {
    return this.querySelector('.complete-with__image');
  }

  get colorTrigger() {
    return this.querySelector('.complete-with__color-trigger');
  }
}

customElements.define('complete-with-section', CompleteWithSectionHandler);

class VariantBottomSheet extends HTMLElement {
  constructor() {
    super();
    this.viewModel = null;
    this.variants = [];
    this.currentSize = null;
    this._onInputChange = this._onInputChange.bind(this);
  }

  connectedCallback() {
    this.viewModel = this.getViewModel();
    const json = this.viewModel.$script?.textContent?.trim();
    this.variants = window.utilities.safeParseJSON(json, []);
    this.initialize();
  }

  disconnectedCallback() {
    this.viewModel.$inputs.forEach(input => {
      input.removeEventListener('change', this._onInputChange);
    });
  }

  getViewModel() {
    return {
      $inputs: [...this.querySelectorAll('input.grid-selector__input')],
      $script: this.querySelector('script[data-variants-bottom-sheet]'),
      $modal: this.closest('modal-slideout'),
    };
  }

  initialize() {
    this.viewModel.$inputs.forEach(input => {
      input.addEventListener('change', this._onInputChange);
    });

    this._maybeUnselectSizeInputs();
  }

  _onInputChange(event) {
    this._handleSizeSelection(event.target);

    this.updateOptionAvailability();

    const variant = this.getResolvedVariant();

    this._handleLowStockMessaging(variant)

    if (this.connectedCard) {
      this.connectedCard.dispatchEvent(new CustomEvent('variant:updated', {
        detail: { variant, size: this.getSelectedValues() },
        bubbles: true
      }));
    }
  }

  updateOptionAvailability() {
    // Don't change anything until size is selected
    if (!this.isSizeSelected) return;

    const selectedValues = this.getSelectedValues();
    const optionCount = selectedValues.length;

    this.viewModel.$inputs.forEach(input => {
      input.disabled = false;
      input.classList.remove('is-unavailable');
    });

    const inputsByOption = this.groupInputsByOptionIndex();

    inputsByOption.forEach((inputsInGroup, optionIndex) => {
      inputsInGroup.forEach(input => {
        const testValue = input.value;
        const matchValues = [...selectedValues];
        matchValues[optionIndex] = testValue;

        const matchingVariant = this.variants.find(variant => {
          return variant.options.length === optionCount &&
            variant.options.every((val, i) => matchValues[i] === val);
        });

        if (!matchingVariant || !matchingVariant.available || matchingVariant.inventory_quantity === 0) {
          input.classList.add('is-unavailable');
        }
      });
    });
  }

  getSelectedValues() {
    const radios = this.viewModel.$inputs.filter(i => i.checked);
    return radios.map(i => i.value);
  }

  getResolvedVariant() {
    const selectedValues = this.getSelectedValues();
    if (selectedValues.length === 0) return null;

    return this.variants.find(v =>
      v.options.length === selectedValues.length &&
      v.options.every((val, i) => val === selectedValues[i])
    );
  }

  groupInputsByOptionIndex() {
    const groups = [];
    this.viewModel.$inputs.forEach(input => {
      const index = window.utilities.toNumber(input?.dataset?.optionPosition) ?? 0;

      if (!groups[index]) groups[index] = [];
      groups[index].push(input);
    });
    return groups;
  }

  _handleLowStockMessaging(variant) {
    const $lowStockMessage = this.closest('modal-slideout')?.querySelector('.product-low-stock-messaging')
    const $lowStockMessageText = $lowStockMessage?.querySelector('.low-inventory-message--text');
    if (!$lowStockMessage || !$lowStockMessageText) return;

    const lowStockThreshold = window?.product?.lowStockValue ?? 0;

    if (variant.inventory_quantity <= 0 && variant.available == false) {
      const sizeOptionValue = variant.options?.at(-1);
      const oosTranslation = window.product.out_of_stock_variant?.replace('{{ size_value }}', sizeOptionValue) ?? '';
      $lowStockMessageText.textContent = oosTranslation;
      $lowStockMessage.classList.remove('hidden');

    } else if (variant.inventory_quantity < lowStockThreshold) {
      const lowStockTranslation = window.product.lowstock.replace('{{ quantity }}', lowStockThreshold) ?? '';
      $lowStockMessageText.textContent = lowStockTranslation;
      $lowStockMessage.classList.remove('hidden');
    } else {
      $lowStockMessageText.textContent = "";
      $lowStockMessage.classList.add('hidden');
    }
  }

  _handleSizeSelection(target) {
    if (!target || !target.name) return;

    const $currentSelectionLabel = target.closest('.product-form__input')?.querySelector('.product-selector-label-current-selection');
    if ($currentSelectionLabel) {
      window.utilities.replaceTextOnly($currentSelectionLabel, " " + target.value)
    }
  
    const isSizeOption = target.closest('#product-form-size-option');

    // Don't set selected size when is unavailable
    if (isSizeOption && !target.classList.contains('is-unavailable')) {
      this.currentSize = target.value;
      this.setAttribute('data-size-selected', 'true');
    }
  }

  _maybeUnselectSizeInputs() {
    const input = this.querySelector('#product-form-size-option input[type="radio"]:checked');
    if (!input) return;

    input.checked = false;
    input.removeAttribute('checked');
    input.setAttribute('aria-checked', 'false');
  }

  get isSizeSelected() {
    return this.getAttribute('data-size-selected') === 'true';
  }

  get connectedCard() {
    return document.querySelector(`[data-id-buy-stack-connector="${this.viewModel.$modal.id}"]`);
  }
}

customElements.define('variants-bottom-sheet', VariantBottomSheet);

class VariantBottomSheetCart extends VariantBottomSheet {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.selectVariantById();
  }
  // Overridden: intentionally left blank
  _maybeUnselectSizeInputs() { }

  selectVariantById() {
    const $triggerElement = document.querySelector(`edit-cart-size[data-modal-id=${this.viewModel.$modal.id}]`);
    const variantId = window.utilities.toNumber($triggerElement?.dataset?.variantId);
    const variant = this.variants.find(v => v.id === variantId);

    if (!variant) return;

    this.viewModel.$inputs.forEach(input => {
      input.checked = false;
      input.removeAttribute('checked');
      input.setAttribute('aria-checked', 'false');
    });
  
    const optionInputs = this.groupInputsByOptionIndex();
    let lastSelectedInput = null;

    variant.options.forEach((optionValue, index) => {
      const group = optionInputs[index];
      if (!group) return;
  
      const inputToSelect = group.find(input => input.value === optionValue);

      if (inputToSelect) {
        inputToSelect.checked = true;
        inputToSelect.setAttribute('checked', 'checked');
        inputToSelect.setAttribute('aria-checked', 'true');

        lastSelectedInput = inputToSelect;
      }
    });

      // Dispatch only once — for the last input selected
      if (lastSelectedInput) {
        lastSelectedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
  }
}

customElements.define('variants-bottom-sheet-cart', VariantBottomSheetCart);
class EditCartSize extends HTMLElement {
  constructor() {
    super();
    this.viewModel = this.getViewModel();
    this.variant = this.dataset.variantId;
    this.selectedVariant = null;

    // Bindings for cleanup
    this._onClick = this._onClick.bind(this);
    this._onVariantUpdated = this._onVariantUpdated.bind(this);
  }

  connectedCallback() {
    this.editCartSizeSubmit?.addEventListener('click', this._onClick);
    this.addEventListener('variant:updated', this._onVariantUpdated);
  }

  _onClick() {
    const { $bottomSheetModal } = this.viewModel;

    if (this.selectedVariant && this.selectedVariant !== +this.variant) {
      this.editCartSizeSubmit.classList.add('is-loading');

      document.dispatchEvent(new CustomEvent('cart:force-refresh', {
        detail: {
          lineIndex: this.dataset.lineIndex,
          originalVariantId: this.variant,
          newVariantId: this.selectedVariant,
          quantity: this.dataset.qty,
          bottomSheetModal: $bottomSheetModal
        }
      }));
    } else {
      $bottomSheetModal.close();
    }
  }

  _onVariantUpdated(e) {
    const { variant } = e.detail;

    if (variant && (!variant.available || variant.inventory_quantity === 0)) {
      this.setButtonOutOfStock();
      return;
    }
  
    this.setButtonAvailable();
    this.selectedVariant = variant.id;
  }

  disconnectedCallback() {
    this.editCartSizeSubmit?.removeEventListener('click', this._onClick);
    this.viewModel.$bottomSheetModal.removeEventListener('variant:updated', this._onVariantUpdated);
  }

  setButtonText(text) {
    this.editCartSizeSubmitLabel.textContent = text;
  }

  setButtonOutOfStock() {
    this.setButtonText(window.variantStrings.outOfStock || 'Out of Stock');
    this.editCartSizeSubmit.setAttribute('disabled', 'true');
  }

  setButtonAvailable() {
    this.setButtonText(window.variantStrings.done || 'Done');
    this.editCartSizeSubmit.removeAttribute('disabled');
  }

  getViewModel() {
    return {
      $bottomSheetModal: document.getElementById(this.dataset.modalId),
      $detailsHolder: this.closest('.cart-item__details-holder'),
    };
  }

  get editCartSizeSubmit() {
    return this.viewModel.$bottomSheetModal.querySelector('[data-cart-edit-size-done]');
  }

  get editCartSizeSubmitLabel() {
    return this.editCartSizeSubmit.querySelector('.shared-button__leading-label');
  }
}

customElements.define('edit-cart-size', EditCartSize);

class EditCartColor extends HTMLElement {
  constructor() {
    super();
    this.viewModel = this.getViewModel();
    this.variant = this.dataset.variantId;
    this.selectedVariant = null;
    this.$selectedSwatch = null;

    // Bindings for cleanup
    this._onClick = this._onClick.bind(this);
    this._onVariantUpdated = this._onVariantUpdated.bind(this);
    this._swatchHandlers = [];
  }

  connectedCallback() {
    this.viewModel.$bottomSheetModal.querySelectorAll('.color-selector').forEach((swatch) => {
      const handler = () => this._onVariantUpdated(swatch);
      swatch.addEventListener('click', handler);
      this._swatchHandlers.push({ swatch, handler });
    });

    this.$selectedSwatch = this.viewModel.$bottomSheetModal.querySelector('.color-selector.is-checked')
    this.patchModalClose();

    this.editCartColorSubmit?.addEventListener('click', this._onClick);
    this.addEventListener('variant:updated', this._onVariantUpdated);
  }

  _onClick() {
    const { $bottomSheetModal } = this.viewModel;

    if (this.selectedVariant && this.selectedVariant !== this.variant) {
      this.editCartColorSubmit.classList.add('is-loading');

      document.dispatchEvent(new CustomEvent('cart:force-refresh', {
        detail: {
          lineIndex: this.dataset.lineIndex,
          originalVariantId: this.variant,
          newVariantId: this.selectedVariant,
          quantity: this.dataset.qty,
          bottomSheetModal: $bottomSheetModal
        }
      }));
    } else {
      $bottomSheetModal.close();
    }
  }

  _onVariantUpdated(swatch) {
    const variantId = swatch.getAttribute('data-variant-id');
    this.selectedVariant = variantId;
  }

  disconnectedCallback() {
    this._swatchHandlers.forEach(({ swatch, handler }) => {
      swatch.removeEventListener('click', handler);
    });

    this.editCartColorSubmit?.removeEventListener('click', this._onClick);
  }

  patchModalClose() {
    const modal = this.viewModel.$bottomSheetModal;
    if (!modal || typeof modal.close !== 'function') return;

    if (this._originalModalClose) return;

    this._originalModalClose = modal.close;

    modal.close = (...args) => {
      this.$selectedSwatch?.click();

      return this._originalModalClose.apply(modal, args);
    };
  }

  getViewModel() {
    return {
      $bottomSheetModal: document.getElementById(this.dataset.modalId)
    };
  }

  get editCartColorSubmit() {
    return this.viewModel.$bottomSheetModal.querySelector('[data-cart-edit-color-done]');
  }
}

customElements.define('edit-cart-color', EditCartColor);
